import { useState, useCallback } from 'react'
import axios from 'axios'
import notify from '../utils/notify'

interface NavigationItem {
	model: string
	text: string
	desc?: string
	filter?: string
	dashboard?: boolean
	folder?: string
}

export function useFavorites(folder: string) {
	const [favorites, setFavorites] = useState<{ [key: string]: boolean }>({})

	const checkAllFavoritesStatus = useCallback(
		async (models: NavigationItem[]) => {
			try {
				const modelList = models.map(item => ({
					folder: item.folder || folder,
					model: item.model,
				}))

				const response = await axios.post(`${API_FIBER_URL}/favorites/check-multiple`, {
					models: modelList,
				})

				if (response.data.success) {
					const newFavorites: { [key: string]: boolean } = {}
					response.data.favorites.forEach((favorite: any) => {
						const key = `${favorite.folder}-${favorite.model}`
						newFavorites[key] = favorite.isFavorite
					})
					setFavorites(newFavorites)
				}
			} catch (error) {
				console.error('Error checking favorite status:', error)
			}
		},
		[folder]
	)

	const toggleFavorite = async (item: NavigationItem, e: any) => {
		e?.event?.preventDefault()
		e?.event?.stopPropagation()

		const itemFolder = item.folder || folder
		const favoriteKey = `${itemFolder}-${item.model}`
		const isCurrentlyFavorite = favorites[favoriteKey]

		try {
			if (isCurrentlyFavorite) {
				await axios.delete(`${API_FIBER_URL}/favorites/remove?folder=${itemFolder}&model=${item.model}`)
				setFavorites(prev => ({
					...prev,
					[favoriteKey]: false,
				}))
				notify('Form removed from favorites', 'success')
			} else {
				await axios.post(`${API_FIBER_URL}/favorites/add`, {
					folder: itemFolder,
					model: item.model,
					text: item.text,
					desc: item.desc,
					filter: item.filter,
					dashboard: item.dashboard || false,
				})
				setFavorites(prev => ({
					...prev,
					[favoriteKey]: true,
				}))
				notify('Form added to favorites', 'success')
			}
		} catch (error: any) {
			const message = error.response?.data?.message || 'Error updating favorites'
			notify(message, 'error')
		}
	}

	const isFavorite = (model: string, itemFolder?: string) => {
		const key = itemFolder ? `${itemFolder}-${model}` : `${folder}-${model}`
		return favorites[key] || false
	}

	return {
		favorites,
		checkAllFavoritesStatus,
		toggleFavorite,
		isFavorite,
	}
}
